<template>
  <div class="bg-white dark:bg-secondary-800 rounded-lg overflow-hidden h-full flex flex-col">
    <!-- Loading -->
    <div v-if="isLoadingExcel" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      <span class="ml-3 text-sm text-secondary-500 dark:text-secondary-400">Cargando vista previa...</span>
    </div>

    <!-- Error -->
    <div v-else-if="excelError" class="flex flex-col items-center justify-center py-16 text-center">
      <FileSpreadsheet class="h-16 w-16 text-red-400 mb-4" />
      <p class="text-sm text-red-600 dark:text-red-400">{{ excelError }}</p>
    </div>

    <!-- Data -->
    <div v-else-if="excelData" class="flex flex-col h-full">
      <!-- Sheet selector & info -->
      <div class="flex items-center justify-between px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-b border-secondary-200 dark:border-secondary-700 flex-shrink-0">
        <div class="flex items-center gap-3">
          <FileSpreadsheet class="h-5 w-5 text-emerald-600" />
          <span class="text-sm font-medium text-secondary-700 dark:text-secondary-300">
            <template v-if="excelData.preview.truncated">
              Mostrando {{ excelData.preview.rows.length }} de {{ excelData.preview.totalRows }} filas
            </template>
            <template v-else>{{ excelData.preview.totalRows }} filas</template>
          </span>
          <button
            v-if="excelData.preview.truncated"
            @click="showMoreRows"
            class="px-2.5 py-1 text-xs rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors"
          >
            Mostrar más
          </button>
        </div>
        <div v-if="excelData.sheetNames.length > 1" class="flex items-center gap-1">
          <span class="text-xs text-secondary-500 dark:text-secondary-400 mr-1">Hoja:</span>
          <button
            v-for="sheet in excelData.sheetNames"
            :key="sheet"
            @click="switchSheet(sheet)"
            class="px-2.5 py-1 text-xs rounded-md transition-colors"
            :class="[
              excelData.activeSheet === sheet
                ? 'bg-emerald-600 text-white font-medium'
                : 'bg-secondary-100 text-secondary-600 dark:bg-secondary-700 dark:text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-600'
            ]"
          >
            {{ sheet }}
          </button>
        </div>
      </div>

      <!-- Table: la fila 1 es una fila más, no un encabezado. Promoverla
           mostraba mal cualquier archivo que no empiece con títulos. -->
      <div class="flex-1 overflow-auto">
        <table class="text-sm border-collapse">
          <colgroup>
            <col style="width: 3rem" />
            <col
              v-for="(width, idx) in excelData.preview.colWidthsPx"
              :key="idx"
              :style="width ? { width: `${width}px` } : undefined"
            />
          </colgroup>
          <thead class="bg-secondary-50 dark:bg-secondary-700/50 sticky top-0 z-10">
            <tr>
              <th class="px-2 py-1 text-center text-xs font-medium text-secondary-400 border border-secondary-200 dark:border-secondary-600"></th>
              <th
                v-for="(_, idx) in excelData.preview.colWidthsPx"
                :key="idx"
                class="px-2 py-1 text-center text-xs font-medium text-secondary-400 border border-secondary-200 dark:border-secondary-600"
              >
                {{ columnLabel(idx) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, rowIdx) in excelData.preview.rows"
              :key="rowIdx"
              :style="excelData.preview.rowHeightsPx[rowIdx] ? { height: `${excelData.preview.rowHeightsPx[rowIdx]}px` } : undefined"
            >
              <td class="px-2 py-1 text-center text-xs text-secondary-400 bg-secondary-50 dark:bg-secondary-700/50 border border-secondary-200 dark:border-secondary-600 sticky left-0">
                {{ rowIdx + 1 }}
              </td>
              <td
                v-for="(cell, cellIdx) in row"
                :key="cellIdx"
                :colspan="cell.colspan"
                :rowspan="cell.rowspan"
                class="px-2 py-1 align-top border border-secondary-200 dark:border-secondary-600 whitespace-pre-wrap break-words"
                :class="[
                  cell.align === 'right' ? 'text-right' : 'text-left',
                  // Con relleno del archivo se fuerza texto oscuro: en modo
                  // oscuro quedaba blanco sobre amarillo.
                  cell.bg ? 'text-secondary-900' : 'text-secondary-600 dark:text-secondary-400'
                ]"
                :style="cell.bg ? { backgroundColor: cell.bg } : undefined"
              >
                {{ cell.text }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Visor de Excel compartido por el buzón y las pestañas de documentos de
 * embarque y orden de compra. Cada quien sabe cómo bajar su archivo (Storage,
 * backend con JWT), por eso recibe la función que trae el binario.
 */
import { ref, onMounted } from 'vue'
import { FileSpreadsheet } from 'lucide-vue-next'
import type { SheetPreview } from '@/utils/excelPreview'

const props = defineProps<{
  loadBuffer: () => Promise<ArrayBuffer>
}>()

/**
 * Topes de render. El usuario quiere ver el archivo, no un resumen, así que el
 * tope de filas es alto y ampliable; el de celdas es la guarda dura contra
 * hojas cuyo !ref abarca todo el libro (A1:XFD1048576).
 */
const PREVIEW_ROW_STEP = 500
const MAX_PREVIEW_COLS = 100
const MAX_PREVIEW_CELLS = 20000

interface ExcelData {
  preview: SheetPreview
  sheetNames: string[]
  activeSheet: string
}

const excelData = ref<ExcelData | null>(null)
const isLoadingExcel = ref(false)
const excelError = ref<string | null>(null)
const visibleRowLimit = ref(PREVIEW_ROW_STEP)

// El libro parseado se guarda fuera de `ref`: cambiar de hoja no debe volver a
// descargar el archivo, y hacerlo reactivo en profundidad no aporta nada.
let workbook: any = null

/** A, B, ... Z, AA, AB — la cabecera de columnas de una hoja de cálculo. */
function columnLabel(index: number): string {
  let label = ''
  let n = index
  do {
    label = String.fromCharCode(65 + (n % 26)) + label
    n = Math.floor(n / 26) - 1
  } while (n >= 0)
  return label
}

async function loadExcelPreview() {
  isLoadingExcel.value = true
  excelError.value = null
  try {
    const arrayBuffer = await props.loadBuffer()

    // xlsx-js-style en vez de xlsx: es el que devuelve el relleno de la celda.
    // Se carga bajo demanda para no meterlo en el bundle inicial.
    const XLSX = await import('xlsx-js-style')
    // cellNF trae el formato del archivo y con él `w` (la fecha y el número tal
    // como los muestra Excel). Sin cellDates a propósito: convertir a Date usa
    // la zona local y puede correr la fecha un día.
    workbook = XLSX.read(arrayBuffer, { type: 'array', cellStyles: true, cellNF: true, sheetStubs: true })
    renderSheet(workbook.SheetNames[0])
  } catch {
    excelError.value = 'No se pudo cargar la vista previa del archivo Excel'
    excelData.value = null
    workbook = null
  } finally {
    isLoadingExcel.value = false
  }
}

async function renderSheet(sheetName: string) {
  if (!workbook) return
  const { buildSheetPreview } = await import('@/utils/excelPreview')
  excelData.value = {
    preview: buildSheetPreview(workbook.Sheets[sheetName], {
      maxRows: visibleRowLimit.value,
      maxCols: MAX_PREVIEW_COLS,
      maxCells: MAX_PREVIEW_CELLS,
    }),
    sheetNames: workbook.SheetNames,
    activeSheet: sheetName,
  }
}

function switchSheet(sheetName: string) {
  // El libro ya está en memoria: antes cada cambio de hoja volvía a descargar
  // el archivo completo.
  visibleRowLimit.value = PREVIEW_ROW_STEP
  renderSheet(sheetName)
}

function showMoreRows() {
  visibleRowLimit.value += PREVIEW_ROW_STEP
  if (excelData.value) renderSheet(excelData.value.activeSheet)
}

onMounted(loadExcelPreview)
</script>
